import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type { Account } from '../../hooks/useAccounts';
import { useAuth } from '../../context/AuthContext';
import useHousehold from '../../hooks/useHousehold';
import { authFetch } from '../../lib/authFetch';

type ManagedAssetType = 'checking' | 'savings';

interface Props {
  open: boolean;
  onClose: () => void;
  accountType: ManagedAssetType;
  accounts: Account[];
  token: string | null;
  onSaved: () => void;
}

const titleByType: Record<ManagedAssetType, string> = {
  checking: 'Checking',
  savings: 'Savings',
};

const ManageAssetAccountsModal: React.FC<Props> = ({
  open,
  onClose,
  accountType,
  accounts,
  token,
  onSaved,
}) => {
  const { auth } = useAuth();
  const { loading: householdLoading, isEmpty: householdEmpty } = useHousehold();
  const [newName, setNewName] = useState('');
  const [newOpeningBalance, setNewOpeningBalance] = useState('');
  const [newAccessScope, setNewAccessScope] = useState<'private' | 'household'>('private');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const accountLabel = titleByType[accountType];
  const isChildUser = auth.user?.role === 'child';
  const canShareWithHousehold = !isChildUser && !householdLoading && !householdEmpty;
  const sharingHelperText = isChildUser
    ? 'Only Primary/Partner can change sharing.'
    : householdEmpty
      ? 'If you need household sharing enabled, please contact support.'
      : householdLoading
        ? 'Loading household...'
        : null;

  const handleAddAccount = async () => {
    if (!token) return;

    const trimmedName = newName.trim();
    const trimmedOpeningBalance = newOpeningBalance.trim();
    const parsedOpeningBalance = Number(trimmedOpeningBalance);
    if (!trimmedName) {
      setError('Name is required.');
      return;
    }
    if (!trimmedOpeningBalance) {
      setError('Current balance as of today is required.');
      return;
    }
    if (Number.isNaN(parsedOpeningBalance)) {
      setError('Current balance as of today must be a valid number.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const res = await authFetch('/api/accounts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: trimmedName,
          openingBalance: parsedOpeningBalance,
          accountType,
          accountCategory: 'asset',
          accessScope: canShareWithHousehold ? newAccessScope : 'private',
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({} as { message?: string }));
        throw new Error(data.message || `Failed to create ${accountLabel.toLowerCase()} account`);
      }

      setNewName('');
      setNewOpeningBalance('');
      setNewAccessScope('private');
      onSaved();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : `Failed to create ${accountLabel.toLowerCase()} account`
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {`Manage ${accountLabel}`}
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography fontWeight={600} sx={{ mb: 1 }}>
          {`Add ${accountLabel} Account`}
        </Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            label="Name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <TextField
            fullWidth
            size="small"
            label="Current balance as of today"
            helperText="Snapshot amount used as today's baseline."
            type="number"
            required
            value={newOpeningBalance}
            onChange={(e) => setNewOpeningBalance(e.target.value)}
          />
          <Button variant="contained" onClick={handleAddAccount} disabled={saving || !token}>
            Add
          </Button>
        </Stack>
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.25, mb: 2 }}>
          <FormControl component="fieldset" disabled={isChildUser || saving} fullWidth>
            <FormLabel component="legend">Sharing</FormLabel>
            <RadioGroup
              value={newAccessScope}
              onChange={(event) =>
                setNewAccessScope(event.target.value as 'private' | 'household')
              }
            >
              <FormControlLabel
                value="private"
                control={<Radio />}
                label="Private (only me)"
              />
              <FormControlLabel
                value="household"
                control={<Radio />}
                label="Shared with household"
                disabled={!canShareWithHousehold}
              />
            </RadioGroup>
          </FormControl>
          {sharingHelperText && (
            <Typography variant="body2" color="text.secondary">
              {sharingHelperText}
            </Typography>
          )}
        </Box>

        <Typography fontWeight={600} sx={{ mb: 1 }}>
          {`Existing ${accountLabel} Accounts`}
        </Typography>
        {accounts.length === 0 ? (
          <Typography color="text.secondary">{`No ${accountLabel.toLowerCase()} accounts yet.`}</Typography>
        ) : (
          <Stack spacing={1}>
            {accounts.map((account) => (
              <Box
                key={account._id}
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.25 }}
              >
                <Typography fontWeight={600}>{account.name}</Typography>
              </Box>
            ))}
          </Stack>
        )}

        {error && (
          <Typography color="error.main" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ManageAssetAccountsModal;
